import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';
import { enablePushNotifications } from '../../../lib/push';

type Convo = {
  id: string;
  phone_e164: string;
  contact_name: string | null;
  customer_id: string | null;
  lead_score: number;
  lead_label: string;
  intent_summary: string | null;
  product_interests: string[] | null;
  needs_human: boolean;
  escalation_reason: string | null;
  status: string;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  message_count: number;
  takeover_by_user_id: string | null;
  takeover_at: string | null;
  customer?: { business_name: string } | null;
};

type Message = {
  id: string;
  direction: 'inbound' | 'outbound';
  sender: string;
  message_type: string;
  body: string | null;
  ai_tool_calls: any[] | null;
  status: string;
  created_at: string;
};

const LABEL_COLORS: Record<string, string> = {
  caliente: '#DC2626',
  tibio: '#F59E0B',
  frio: '#3B82F6',
  nuevo: '#6B7280',
  cliente_existente: '#10B981',
  cerrado_ganado: '#059669',
  cerrado_perdido: '#9CA3AF',
};

export function AdminWhatsApp() {
  const [convos, setConvos] = useState<Convo[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'human_needed' | 'hot'>('all');
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewConvo, setShowNewConvo] = useState(false);

  const loadConvos = async () => {
    setLoading(true);
    let q = supabase.from('whatsapp_conversations')
      .select('id, phone_e164, contact_name, customer_id, lead_score, lead_label, intent_summary, product_interests, needs_human, escalation_reason, status, last_inbound_at, last_outbound_at, message_count, takeover_by_user_id, takeover_at, customer:customers(business_name)')
      .eq('status', 'active')
      .order('lead_score', { ascending: false });
    if (filter === 'human_needed') q = q.eq('needs_human', true);
    if (filter === 'hot') q = q.gte('lead_score', 70);
    const { data } = await q.limit(50);
    setConvos((data as any) ?? []);
    setLoading(false);
  };

  const loadMessages = async (convoId: string) => {
    const { data } = await supabase.from('whatsapp_messages')
      .select('id, direction, sender, message_type, body, ai_tool_calls, status, created_at')
      .eq('conversation_id', convoId)
      .order('created_at', { ascending: true });
    setMessages((data as any) ?? []);
  };

  useEffect(() => { loadConvos(); }, [filter]);
  useEffect(() => {
    if (selectedId) loadMessages(selectedId);
  }, [selectedId]);

  // Realtime subscriptions
  useEffect(() => {
    const ch = supabase.channel('whatsapp_admin')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_messages' }, () => {
        if (selectedId) loadMessages(selectedId);
        loadConvos();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_conversations' }, loadConvos)
      .subscribe();
    return () => { ch.unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const takeover = async () => {
    if (!selectedId) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from('whatsapp_conversations').update({
      takeover_by_user_id: user.id,
      takeover_at: new Date().toISOString(),
      needs_human: false,
    }).eq('id', selectedId);
    await loadConvos();
  };

  const releaseAgent = async () => {
    if (!selectedId) return;
    await supabase.from('whatsapp_conversations').update({
      takeover_by_user_id: null,
      takeover_at: null,
    }).eq('id', selectedId);
    await loadConvos();
  };

  const sendMessage = async () => {
    if (!composeText.trim() || !selectedId) return;
    setSending(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ conversation_id: selectedId, text: composeText.trim() }),
    });
    const data = await res.json();
    setSending(false);
    if (data.ok || data.conversation_id) {
      setComposeText('');
      await loadMessages(selectedId);
      await loadConvos();
    } else {
      alert('Error al enviar: ' + (data.error ?? 'desconocido'));
    }
  };

  const startNewConvo = async (phone: string, text: string) => {
    setSending(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ phone, text }),
    });
    const data = await res.json();
    setSending(false);
    if (data.conversation_id) {
      setShowNewConvo(false);
      setSelectedId(data.conversation_id);
      await loadConvos();
    } else {
      alert('Error: ' + (data.error ?? 'no se pudo iniciar'));
    }
  };

  const sel = convos.find(c => c.id === selectedId);

  return (
    <AdminContainer eyebrow="Marketing & Ventas" title="WhatsApp Sales Agent">
      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 220px)' }}>
        {/* Lista conversaciones */}
        <div style={{
          width: 360,
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 12px 8px', borderBottom: `1px solid ${theme.border}` }}>
            <button onClick={() => setShowNewConvo(true)} style={{
              width: '100%',
              background: theme.accent,
              color: theme.brand,
              border: 'none',
              padding: '10px 14px',
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              cursor: 'pointer',
              fontFamily: theme.font.sans,
              marginBottom: 10,
            }}>
              + Nueva conversación
            </button>
            <button onClick={async () => {
              const r = await enablePushNotifications();
              alert(r.ok
                ? '🔔 Notificaciones activadas. Te avisamos cuando un lead necesite atención.'
                : 'No se pudo activar: ' + (r.error ?? ''));
            }} style={{
              width: '100%', background: 'transparent', color: theme.brand,
              border: `1px solid ${theme.borderStrong}`, padding: '8px 14px',
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
              cursor: 'pointer', fontFamily: theme.font.sans, marginBottom: 10,
            }}>
              🔔 Activar notificaciones
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              <FilterBtn active={filter === 'all'} onClick={() => setFilter('all')}>Todas</FilterBtn>
              <FilterBtn active={filter === 'hot'} onClick={() => setFilter('hot')}>🔥 Hot</FilterBtn>
              <FilterBtn active={filter === 'human_needed'} onClick={() => setFilter('human_needed')}>👤</FilterBtn>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 24, color: theme.textMuted, textAlign: 'center' }}>Cargando…</div>
            ) : convos.length === 0 ? (
              <div style={{ padding: 24, color: theme.textMuted, textAlign: 'center', lineHeight: 1.6 }}>
                Sin conversaciones todavía.<br />
                Cuando un cliente escriba a tu WhatsApp Business, aparecerá aquí con score AI.
              </div>
            ) : convos.map(c => (
              <ConvoItem
                key={c.id}
                convo={c}
                selected={selectedId === c.id}
                onClick={() => setSelectedId(c.id)}
              />
            ))}
          </div>
        </div>

        {/* Vista detalle */}
        <div style={{
          flex: 1,
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {!sel ? (
            <div style={{ padding: 48, textAlign: 'center', color: theme.textMuted }}>
              Selecciona una conversación
            </div>
          ) : (
            <>
              {/* Header conversación */}
              <div style={{
                padding: '14px 18px',
                borderBottom: `1px solid ${theme.border}`,
                background: theme.bgSoft,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, fontWeight: 500 }}>
                      {sel.customer?.business_name || sel.contact_name || sel.phone_e164}
                    </div>
                    <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
                      {sel.phone_e164} · {sel.message_count} mensajes
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <ScoreBadge score={sel.lead_score} label={sel.lead_label} />
                    {sel.needs_human ? (
                      <button onClick={takeover} style={takeBtn}>👤 Tomar control</button>
                    ) : (
                      <button onClick={releaseAgent} style={{ ...takeBtn, background: theme.bg, color: theme.brand, border: `1px solid ${theme.border}` }}>
                        🤖 AI activo
                      </button>
                    )}
                  </div>
                </div>
                {sel.intent_summary && (
                  <div style={{ marginTop: 10, fontSize: 12, color: theme.brand, padding: 8, background: theme.bg, border: `1px solid ${theme.border}` }}>
                    <strong>Resumen AI:</strong> {sel.intent_summary}
                  </div>
                )}
                {sel.needs_human && sel.escalation_reason && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#DC2626', padding: 8, background: '#FEE2E2' }}>
                    ⚠️ Escalado: {sel.escalation_reason}
                  </div>
                )}
              </div>

              {/* Timeline mensajes */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {messages.map(m => (
                  <MessageBubble key={m.id} msg={m} />
                ))}
              </div>

              {/* Composer */}
              <div style={{ padding: 12, borderTop: `1px solid ${theme.border}`, background: theme.bg }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <textarea
                    value={composeText}
                    onChange={e => setComposeText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                    }}
                    placeholder="Escribe respuesta... (Enter envía, Shift+Enter nueva línea)"
                    rows={2}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: `1px solid ${theme.border}`,
                      fontSize: 13,
                      fontFamily: theme.font.sans,
                      resize: 'none',
                      outline: 'none',
                      color: theme.text,
                      background: theme.bg,
                    }}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || !composeText.trim()}
                    style={{
                      background: theme.brand,
                      color: theme.textOnBrand,
                      border: 'none',
                      padding: '12px 20px',
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      cursor: sending || !composeText.trim() ? 'not-allowed' : 'pointer',
                      opacity: sending || !composeText.trim() ? 0.5 : 1,
                      fontFamily: theme.font.sans,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sending ? 'Enviando…' : '✈ Enviar'}
                  </button>
                </div>
                <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 6 }}>
                  {sel?.takeover_by_user_id
                    ? '👤 Tú estás controlando esta conversación. El AI está pausado.'
                    : '🤖 AI activo. Cuando envíes, tomas control automáticamente.'}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showNewConvo && (
        <NewConvoModal
          onClose={() => setShowNewConvo(false)}
          onSubmit={startNewConvo}
          sending={sending}
        />
      )}
    </AdminContainer>
  );
}

function NewConvoModal({ onClose, onSubmit, sending }: {
  onClose: () => void;
  onSubmit: (phone: string, text: string) => void;
  sending: boolean;
}) {
  const [phone, setPhone] = useState('+52');
  const [text, setText] = useState('Hola, te escribo de NULED. ');
  const [customers, setCustomers] = useState<{ id: string; business_name: string; primary_phone: string | null }[]>([]);
  const [searchQ, setSearchQ] = useState('');

  useEffect(() => {
    if (searchQ.length < 2) { setCustomers([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('customers')
        .select('id, business_name, primary_phone')
        .or(`business_name.ilike.%${searchQ}%,primary_email.ilike.%${searchQ}%`)
        .limit(8);
      setCustomers((data as any) ?? []);
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: theme.bg, padding: 24, width: 480, maxWidth: '90vw',
        border: `1px solid ${theme.border}`, position: 'relative',
      }} onClick={e => e.stopPropagation()}>
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        <h2 style={{
          fontFamily: theme.font.head, fontSize: 22, fontWeight: 400,
          color: theme.brand, margin: '0 0 16px',
        }}>
          Nueva conversación WhatsApp
        </h2>

        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>Buscar cliente existente</div>
          <input
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            placeholder="Nombre o email del cliente..."
            style={inputStyle}
          />
          {customers.length > 0 && (
            <div style={{ marginTop: 6, border: `1px solid ${theme.border}`, maxHeight: 150, overflowY: 'auto' }}>
              {customers.map(c => (
                <button key={c.id} onClick={() => {
                  setPhone(c.primary_phone ?? '+52');
                  setSearchQ('');
                }} style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  background: 'transparent', border: 'none',
                  borderBottom: `1px solid ${theme.border}`,
                  padding: '8px 10px', fontSize: 12, cursor: 'pointer',
                  fontFamily: theme.font.sans,
                }}>
                  <strong>{c.business_name}</strong>
                  <span style={{ marginLeft: 8, color: theme.textMuted }}>{c.primary_phone ?? 'sin tel'}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>Teléfono destino (E.164 con +)</div>
          <input
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="+5215555011014"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>Mensaje inicial</div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={4}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: theme.font.sans }}
          />
          <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 4 }}>
            ⚠️ Si el cliente NO ha escrito antes a este número, Meta requiere usar plantilla aprobada. Texto libre solo funciona después de que el cliente responda.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${theme.border}`,
            color: theme.brand, padding: '10px 18px', fontSize: 12,
            fontWeight: 600, cursor: 'pointer', fontFamily: theme.font.sans,
          }}>
            Cancelar
          </button>
          <button onClick={() => onSubmit(phone, text)} disabled={sending || !phone || !text} style={{
            background: theme.accent, color: theme.brand, border: 'none',
            padding: '10px 18px', fontSize: 12, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer',
            fontFamily: theme.font.sans,
            opacity: sending || !phone || !text ? 0.5 : 1,
          }}>
            {sending ? 'Enviando…' : 'Iniciar conversación'}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, color: theme.textMuted, marginBottom: 4,
  textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: `1px solid ${theme.border}`,
  fontSize: 13, color: theme.text, background: theme.bg,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};

function ConvoItem({ convo, selected, onClick }: { convo: Convo; selected: boolean; onClick: () => void }) {
  const lastActivity = convo.last_inbound_at || convo.last_outbound_at;
  return (
    <button onClick={onClick} style={{
      width: '100%',
      background: selected ? theme.bgSoft : theme.bg,
      border: 'none',
      borderBottom: `1px solid ${theme.border}`,
      borderLeft: selected ? `3px solid ${theme.accent}` : '3px solid transparent',
      padding: '12px 14px',
      textAlign: 'left',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.brand, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {convo.customer?.business_name || convo.contact_name || convo.phone_e164}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
            {convo.intent_summary?.slice(0, 70) || 'Sin contexto aún'}
          </div>
        </div>
        <ScoreBadge score={convo.lead_score} label={convo.lead_label} small />
      </div>
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 10, color: theme.textMuted }}>
          {convo.message_count} msj · {lastActivity ? new Date(lastActivity).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
        </div>
        {convo.needs_human && <span style={{ fontSize: 10, color: '#DC2626', fontWeight: 700 }}>👤 HUMANO</span>}
      </div>
    </button>
  );
}

function ScoreBadge({ score, label, small }: { score: number; label: string; small?: boolean }) {
  const color = LABEL_COLORS[label] || '#6B7280';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: color,
      color: 'white',
      padding: small ? '2px 6px' : '4px 10px',
      fontSize: small ? 9 : 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      borderRadius: 3,
    }}>
      {score} · {label}
    </div>
  );
}

function MessageBubble({ msg }: { msg: Message }) {
  const isInbound = msg.direction === 'inbound';
  const isAI = msg.sender === 'agent_ai';
  return (
    <div style={{
      alignSelf: isInbound ? 'flex-start' : 'flex-end',
      maxWidth: '70%',
      background: isInbound ? '#F3F4F6' : isAI ? '#DCFCE7' : '#DBEAFE',
      padding: '8px 12px',
      borderRadius: 12,
      fontSize: 13,
      lineHeight: 1.4,
    }}>
      <div style={{ fontSize: 9, color: theme.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {isInbound ? '📥 cliente' : isAI ? '🤖 agente AI' : '👤 humano'}
        {' · '}{new Date(msg.created_at).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit' })}
        {msg.status === 'failed' && <span style={{ color: '#DC2626' }}> · ❌ falló</span>}
      </div>
      <div style={{ whiteSpace: 'pre-wrap', color: theme.text }}>
        {msg.body || `[${msg.message_type}]`}
      </div>
      {msg.ai_tool_calls && msg.ai_tool_calls.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono }}>
          🔧 {msg.ai_tool_calls.map(t => t.name).join(', ')}
        </div>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      flex: 1,
      background: active ? theme.brand : 'transparent',
      color: active ? theme.textOnBrand : theme.brand,
      border: `1px solid ${active ? theme.brand : theme.border}`,
      padding: '6px 10px',
      fontSize: 11,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>
      {children}
    </button>
  );
}

const takeBtn: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '8px 14px',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  cursor: 'pointer',
  fontFamily: theme.font.sans,
};
